import { useEffect, useState } from 'react'
import { useAuth } from '@/context/AuthContext'
import { useServices } from '@/context/ServiceContext'
import type { Organization } from '@/domain/organization'

// P2 org-isolation guard used by every management view (Team dashboard,
// Learner Management, Leaderboard). For every role except super_admin, the
// resolved orgId is ALWAYS currentUser.orgId — it never reads a route param
// or other user-controlled input, so an org_admin/teacher literally cannot
// be steered into viewing another organization's data (Document 11 §5,
// P2 privacy requirement). super_admin is the only role allowed to switch,
// and only among the platform's real seeded organizations.
export function useManagedOrg() {
  const { currentUser } = useAuth()
  const { organizations } = useServices()
  const isSuperAdmin = currentUser?.role === 'super_admin'

  const [orgs, setOrgs] = useState<Organization[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const [loadingOrgs, setLoadingOrgs] = useState(isSuperAdmin)

  useEffect(() => {
    if (!isSuperAdmin) return
    let cancelled = false
    organizations.list().then((list) => {
      if (cancelled) return
      setOrgs(list)
      setSelectedOrgId((prev) => prev ?? list[0]?.id ?? null)
      setLoadingOrgs(false)
    })
    return () => {
      cancelled = true
    }
  }, [isSuperAdmin, organizations])

  const orgId = isSuperAdmin ? selectedOrgId : currentUser?.orgId ?? null

  return { orgId, orgs, isSuperAdmin, selectedOrgId, setSelectedOrgId, loadingOrgs }
}
